import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    CircularProgress,
    Divider,
    Popover,
    Box,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    IconButton,
    Typography
} from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import _ from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { ADD_NOTIFICATION } from "../../../../../store/types";
import { getAccountQuestions, removeArchivedQuestions, saveSelectedQuestions } from "../../../../../services/questions.service";
import QuestionAccordion from "./components/QuestionAccordion";
import { isFullAccess, isOnlyHospitalTabAccess, selectedTabAccess, mapQuestionToValues } from "../../../../../utilis/common";
import { PAGE_TYPE } from "../../../../../types/pages.type";
import styles from "./QuestionSetup.module.scss";
import AddQuestionDialog from "./AddQuestion";
import { QUESTION_INPUT_TYPE, QUESTIONS_TEMPLATE_TYPE, TIME_RANGE_TYPE } from "../../../../../types/question.type";
import api from "../../../../../services/api/api";
import { updateQuestionLabel } from "../../../../../services/api/data-input.api";
import useResetDynamicCards from "../../../../hooks/useResetDynamicCards";
import useFacilityId from "../../../../../hooks/useFacilityId";
import useAccountId from "../../../../../hooks/useAccountId";
import { AUTH_ROLES } from "../../../../../types/auth.type";
import { ADT_SUB_TYPES } from "../../../../../types/common.type";
import AlertDialog from "../../../../shared/AlertDialog";
import { clearCachedResults as clearCachedResultsHospital } from "../../../../../store/reducers/hospital.slice";
import { clearCachedResults as clearCachedResultsAdmission } from "../../../../../store/reducers/admission.slice";
import { clearCachedResults as clearCachedResultsCommunity } from "../../../../../store/reducers/community-transfer.slice";
import { clearCachedResults as clearCachedResultsDeceased } from "../../../../../store/reducers/deceased.slice";
import { clearCachedResults as clearCachedResultsOverall } from "../../../../../store/reducers/overall.slice";

const initialValues = {
    label: "",
    tableLabel: "",
    isCustom: false,
    customQuestionInputType: "",
    customQuestionOptions: [],
    numberRangeType: null,
    isArchived: false,
    timeRangeType: TIME_RANGE_TYPE.HOURLY,
    templateType: QUESTIONS_TEMPLATE_TYPE.DX_CARD,
    autoFillEnabled: false,
    sourceTransitionType: '',
    sourceQuestionId: '',
    dateAnalysisStartQuestion: '',
    dateAnalysisEndQuestion: '',
    useCustomCardInDashboard: false,
    description: '',
    aiDescription: '',
    existingAccessor: '',
    shareUserIds: [],
    invisibleAnswerOptions: false,
    dataInputLabel: '',
    isUniversal: false,
}

const ArchivedQuestionsList = ({ onClose, refreshParent, defaultType, defaultTransferType }) => {
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedQuestions, setSelectedQuestions] = useState([]);
    const [type, setType] = useState(defaultType !== undefined ? defaultType : "admission");
    const [transferType, setTransferType] = useState(defaultTransferType !== undefined ? defaultTransferType : "hospitalTransfer");
    const [deletePopover, setDeletePopover] = useState({ open: false, anchorEl: null, questionId: null });
    const [editQuestion, setEditQuestion] = useState(undefined);
    const [questionValues, setQuestionValues] = useState(initialValues);
    const [showAddQ, setShowAddQ] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState(false);
    const [isArchivedDialog, setIsArchivedDialog] = useState(false);
    const [timeFieldQuestions, setTimeFieldQuestions] = useState([]);

    const { permission } = useSelector(
        ({ auth, account, facility, permission, activeFacilities }) => ({
            permission
        })
    );
    const { facilityId } = useFacilityId();
    const { accountId: accountIdFromHook } = useAccountId();
    const resetDynamicCards = useResetDynamicCards();
    const dispatch = useDispatch();

    const isOnlyHospitalDashboard = useMemo(() => {
        return isOnlyHospitalTabAccess();
    }, []);

    const resetQuestionValues = () => {
        setQuestionValues(initialValues);
    }

    const transferOptions = useMemo(() => {
        const selectedTab = selectedTabAccess();
        return [
            ...(_.includes(selectedTab, PAGE_TYPE.HOSPITAL) ? [{ label: "Hospital Transfer", value: "hospitalTransfer" }] : []),
            ...(_.includes(selectedTab, PAGE_TYPE.COMMUNITY_TRANSFER) ? [
                { label: "Total Community", value: "totalCommunity" },
                { label: "Safe Discharge", value: "safeDischarge" },
                { label: "SNF", value: "SNF" },
                { label: "AMA", value: "AMA" }
            ] : []),
            ...(_.includes(selectedTab, PAGE_TYPE.DECEASED) ? [{ label: "Deceased", value: "deceased" }] : []),
        ];
    }, []);

    const fetchQuestions = useCallback(async () => {
        setLoading(true);
        try {
            let transferTypeArr = [transferType];
            const res = await getAccountQuestions({
                forType: type,
                ...(type === "transfer" && { forTransferType: transferTypeArr }),
            });
            setQuestions(res || []);

            const timeFieldQs = res?.filter(ele =>
                ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RESIDENT_LIST ||
                (!ele?.question?.customQuestionInputType && ele?.question?.type === "time") ||
                (ele?.question?.customQuestionInputType === QUESTION_INPUT_TYPE.TIME_TAB_RANGE && !ele?.question?.existingAccessor)
            );
            const timeFieldQsArray = timeFieldQs?.filter(ele => ele?.question?.accessor !== "shiftData");
            setTimeFieldQuestions(timeFieldQsArray ?? []);
        } catch (error) {
            console.error("Failed to fetch archived questions", error);
        } finally {
            setLoading(false);
        }
    }, [type, transferType]);

    useEffect(() => {
        fetchQuestions();
    }, [fetchQuestions]);

    const handleCheckboxChange = (id) => {
        setSelectedQuestions((prev) =>
            prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
        );
    };

    const handleSaveQuestions = async () => {
        if (selectedQuestions.length === 0) return;
        try {
            const res = await saveSelectedQuestions({ questionIds: selectedQuestions });
            if (res) {
                dispatch({
                    type: ADD_NOTIFICATION,
                    payload: {
                        label: "Questions saved successfully",
                        type: "success",
                        id: "questionArchivedSaved"
                    }
                });
                refreshParent();
                onClose();
            }
        } catch (error) {
            console.error("Failed to saved questions", error);
        }
    };

    const handleDeleteClick = (event, id) => {
        setDeletePopover({ open: true, anchorEl: event.currentTarget, questionId: id });
    };

    const confirmDelete = async () => {
        try {
            const res = await removeArchivedQuestions(deletePopover.questionId);
            if (res) {
                dispatch({
                    type: ADD_NOTIFICATION,
                    payload: { label: "Question removed permanently", type: "success", id: "removeArchiveQuestions" },
                });
                setQuestions((prev) => prev.filter((q) => q._id !== deletePopover.questionId));
                setSelectedQuestions((prev) => prev.filter((id) => id !== deletePopover.questionId));
            }
        } catch (error) {
            console.error("Failed to delete question", error);
        } finally {
            setDeletePopover({ open: false, anchorEl: null, questionId: null });
        }
    };

    const handleEditClick = (event, question) => {
        setEditQuestion(question);
        setQuestionValues(mapQuestionToValues(question));
        setShowAddQ(true);
    };

    const clearCachedResults = () => {
        dispatch(clearCachedResultsHospital());
        dispatch(clearCachedResultsAdmission());
        dispatch(clearCachedResultsCommunity());
        dispatch(clearCachedResultsDeceased());
        dispatch(clearCachedResultsOverall());
    }

    const saveQuestion = async (isCustomQuestion, updatedValues = null, extraParams = {}) => {
        const questionValuesData = updatedValues ? updatedValues : questionValues;
        setLoading(true);
        if (editQuestion?._id) {
            const latestType = isOnlyHospitalDashboard && type !== "return" ? "transfer" : type;

            await updateQuestionLabel(
                {
                    label: questionValuesData.label,
                    tableLabel: questionValuesData.tableLabel,
                    isArchived: questionValuesData?.isArchived ?? false,
                    templateType: questionValuesData?.templateType,
                    timeRangeType: questionValuesData?.timeRangeType,
                    description: questionValuesData?.description,
                    aiDescription: questionValuesData?.aiDescription,
                    forType: latestType,
                    ...(isCustomQuestion && { customData: questionValuesData }),
                    isUniversal: questionValuesData?.isUniversal || false,
                    ...(latestType === "transfer" && { forTransferType: transferType }),
                    ...(editQuestion?.forTransferType && editQuestion?.forTransferType === ADT_SUB_TYPES.TOTAL_COMMUNITY && { mainTransferType: editQuestion?.forTransferType }),
                    ...extraParams,
                },
                editQuestion._id
            );

            resetDynamicCards();
            fetchQuestions();
            setLoading(false);
            setEditQuestion(undefined);
            resetQuestionValues()
            setShowAddQ(false);
            clearCachedResults();
            refreshParent();
        } else {
            await api.post("/api/question/set", {
                facilityId: facilityId,
                label: questionValuesData.label,
                tableLabel: questionValuesData?.tableLabel,
                isArchived: questionValuesData?.isArchived ?? false,
                forType: type,
                forTransferType: transferType,
                active: true,
                type: "text",
                accessor: questionValuesData?.label?.replace(/\s/g, ""),
                allowAfter: true,
                isEditable: true,
                accountId: accountIdFromHook,
                customData: questionValuesData,
                templateType: questionValuesData?.templateType,
            });

            // Note: Since this is ArchivedQuestionsList, adding a NEW question might not be standard,
            // but we keep the logic for compatibility with AddQuestionDialog if it allows adding.

            resetDynamicCards();
            fetchQuestions();
            setLoading(false);
            resetQuestionValues()
            setShowAddQ(false);
            clearCachedResults();
            refreshParent();
        }
    };

    const handleCustomQuestion = () => {
        const isSuperOrAdmin = [AUTH_ROLES.SUPER, AUTH_ROLES.ADMIN].includes(permission?.role);
        if (!isSuperOrAdmin) return false;

        return editQuestion ? !!editQuestion.isCustom : true;
    };

    return (
        <Dialog
            open={true}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: { borderRadius: "12px", minHeight: "500px" }
            }}
        >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                <Typography variant="h6" fontWeight="600">Manage Archive Questions</Typography>
                <IconButton onClick={onClose} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                {showAddQ && (
                    <AddQuestionDialog
                        open={showAddQ}
                        setIsArchivedDialog={setIsArchivedDialog}
                        onClose={() => {
                            setShowAddQ(false);
                            setEditQuestion(undefined);
                            resetQuestionValues();
                        }}
                        onSave={saveQuestion}
                        refreshQuestions={fetchQuestions}
                        editQuestion={editQuestion}
                        loading={loading}
                        setEditQuestion={setEditQuestion}
                        setQuestionValues={setQuestionValues}
                        questionValues={questionValues}
                        setConfirmDialog={setConfirmDialog}
                        resetQuestionValues={resetQuestionValues}
                        isCustomQuestion={handleCustomQuestion()}
                        latestType={type}
                        transferType={transferType}
                        currentQuestions={questions}
                        timeFieldQuestions={timeFieldQuestions}
                        isFromArchiveList={true}
                    />
                )}
                <Box className={styles.questionSetup} sx={{ p: 2, bgcolor: '#f8f9fa' }}>
                    <div className={styles.content}>
                        <div className={`df aic ffmar fs15 fw500 ${styles.tabsWrpr}`} style={{ marginTop: 0, marginBottom: 0 }}>
                            {_.includes(selectedTabAccess(), PAGE_TYPE.ADMISSION) && (
                                <div
                                    className={`df acc ${styles.tab} ${type === "admission" ? styles.active : ""}`}
                                    onClick={() => setType("admission")}
                                >
                                    Admissions
                                </div>
                            )}
                            {isFullAccess() && (
                                <div
                                    className={`df acc ${styles.tab} ${type === "return" ? styles.active : ""}`}
                                    onClick={() => setType("return")}
                                >
                                    Returns
                                </div>
                            )}
                            {[PAGE_TYPE.HOSPITAL, PAGE_TYPE.COMMUNITY_TRANSFER, PAGE_TYPE.DECEASED].some(ele => selectedTabAccess().includes(ele)) && (
                                <div
                                    className={`df acc ${styles.tab} ${type === "transfer" ? styles.active : ""}`}
                                    onClick={() => setType("transfer")}
                                >
                                    Transfers
                                </div>
                            )}
                            {type === "transfer" && (
                                <FormControl sx={{ minWidth: 200, ml: 2 }} size="small">
                                    <InputLabel>Transfer Type</InputLabel>
                                    <Select
                                        value={transferType}
                                        onChange={(e) => setTransferType(e.target.value)}
                                        label="Transfer Type"
                                    >
                                        {transferOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        </div>
                    </div>
                </Box>
                <Box sx={{ flex: 1, p: 3, overflowY: 'auto', minHeight: '300px' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                            <CircularProgress />
                        </Box>
                    ) : questions.length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {questions.map((ele) => (
                                <QuestionAccordion
                                    key={ele._id}
                                    question={ele.question || ele}
                                    isSelected={selectedQuestions.includes(ele._id)}
                                    onCheckboxChange={handleCheckboxChange}
                                    onDeleteClick={handleDeleteClick}
                                    onEditClick={handleEditClick}
                                    secondaryLabel={
                                        (ele.account?.name || ele.question?.account?.name) && (
                                            <Typography variant="caption" color="textSecondary">
                                                Source Account: {ele.account?.name || ele.question?.account?.name}
                                            </Typography>
                                        )
                                    }
                                />
                            ))}
                        </Box>
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                            <Typography color="textSecondary">No archived questions found for this selection.</Typography>
                        </Box>
                    )}
                </Box>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} variant="outlined" color="inherit">
                    Cancel
                </Button>
                <Button
                    onClick={handleSaveQuestions}
                    variant="contained"
                    color="primary"
                    disabled={selectedQuestions.length === 0}
                >
                    Save Selected ({selectedQuestions.length})
                </Button>
            </DialogActions>

            <Popover
                open={deletePopover.open}
                anchorEl={deletePopover.anchorEl}
                onClose={() => setDeletePopover({ open: false, anchorEl: null, questionId: null })}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <Box sx={{ p: 2 }}>
                    <Typography sx={{ mb: 1.5 }}>Are you sure you want to permanently delete this question?</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Button
                            size="small"
                            onClick={() => setDeletePopover({ open: false, anchorEl: null, questionId: null })}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="small"
                            variant="contained"
                            color="error"
                            onClick={confirmDelete}
                        >
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Popover>

            {!!isArchivedDialog && (
                <AlertDialog
                    {...(editQuestion?._id && { isArchived: questionValues?.isArchived ?? false })}
                    content="Archive question?"
                    handleClose={async () => {
                        const updatedValues = {
                            ...questionValues,
                            isArchived: false,
                        };
                        await setQuestionValues(updatedValues);
                        setIsArchivedDialog(false);
                        saveQuestion(handleCustomQuestion(), updatedValues);
                    }}
                    withYesNo={true}
                    handleSubmit={async () => {
                        const updatedValues = {
                            ...questionValues,
                            isArchived: true,
                        };
                        await setQuestionValues(updatedValues);
                        setIsArchivedDialog(false);
                        setConfirmDialog(false);
                        saveQuestion(handleCustomQuestion(), updatedValues);
                    }}
                />
            )}
            {!!confirmDialog && (
                <AlertDialog
                    content="New Question will be display for all account facility."
                    handleClose={() => setConfirmDialog(false)}
                    handleSubmit={async () => {
                        setConfirmDialog(false);
                        if (questionValues?.customQuestionInputType === QUESTION_INPUT_TYPE.DATE_ANALYSIS) {
                            const updatedValues = {
                                ...questionValues,
                                isArchived: false,
                            };
                            await setQuestionValues(updatedValues);
                            setIsArchivedDialog(false);
                            setConfirmDialog(false);
                            saveQuestion(handleCustomQuestion(), updatedValues);
                        } else {
                            setIsArchivedDialog(true);
                        }
                    }}
                />
            )}
        </Dialog>
    );
};

export default ArchivedQuestionsList;
